import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Routes, Route, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { onAuthStateChanged, getRedirectResult } from "firebase/auth";

import { auth, db, logAppCheckToken } from "./components/firebase";
import { collection, getDocs, getDoc, doc } from "firebase/firestore";
import { toast } from "react-toastify";
import { HelmetProvider } from "react-helmet-async";

// Pages
import GettingStarted from "./components/GettingStarted";
import SignIn from "./components/SignIn";
import StartOptions from "./components/StartOptions";
import DiscoverHomes from "./components/DiscoverHomes";
import Register from "./components/Register";
import HomePage from "./components/Homepage";
import Layout from "./components/Layout";
import PGDetails from "./components/PGDetails";
import OwnerCard from "./components/Ownercard";
import Profile from "./components/Profile";
import PGorRentDialog from "./components/PGorRentDialog";
import Inbox from "./components/Inbox";
import PGListView from "./components/PGListView";
import SavedProperties from "./components/SavedProperties";
import RentDetails from "./components/RentDetails";
import RentListView from "./components/RentListView";
import TermsAndConditions from "./components/TermsAndConditions";
import MasterControlPanel from "./components/MasterControlPanel";
import ReportIssue from "./components/ReportIssue";
import HouseBills from "./components/HouseBills";
import HouseListingForm from "./components/HouseListingForm";
import RentListingForm from "./components/RentListingForm";
import BuyPacks from "./components/buypacks";
import AboutUs from "./components/AboutUs";
import NeedSupport from "./components/NeedSupport";
import PGListing from "./components/PGListing";
import RentListing from "./components/RentListing";
import OffersPage from "./components/OffersPage";
import RentSearchResults from "./components/RentSearchResults";
import AeroSearchUI from "./components/AeroSearchUI";
import MyListings from "./components/MyListings";
import SearchResults from "./components/SearchResults";
import ActionPage from "./components/ActionPage";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";

// -----------------------
// Protected Route
// -----------------------
const ProtectedRoute = ({ children, authInitialized }) => {
  if (!authInitialized) return <div style={{ textAlign: "center", marginTop: "50px" }}>Loading...</div>;
  return auth.currentUser ? children : <Navigate to="/signin" />;
};

// -----------------------
// Animated Routes
// -----------------------
function AnimatedRoutes({ authInitialized }) {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>

        {/* PUBLIC ROUTES */}
        <Route path="/" element={<GettingStarted />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/getting-started" element={<GettingStarted />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/register" element={<Register />} />
        <Route path="/start-options" element={<StartOptions />} />
        <Route path="/discover-homes" element={<DiscoverHomes />} />
        <Route path="/pg/:listingId" element={<PGDetails />} />
        <Route path="/owner/:ownerId/:listingId" element={<OwnerCard />} />
        <Route path="/chat-preview" element={<Inbox />} />
        <Route path="/pg-list/:type" element={<PGListView />} />
        <Route path="/saved-properties" element={<SavedProperties />} />
        <Route path="/rent/:listingId" element={<RentDetails />} />
        <Route path="/rent-list-view/:type" element={<RentListView />} />
        <Route path="/master-control" element={<MasterControlPanel />} />
        <Route path="/report-issue" element={<ReportIssue />} />
        <Route path="/admin/housebills" element={<HouseBills />} />
        <Route path="/search-results" element={<SearchResults />} />
        <Route path="/rent-search-results" element={<RentSearchResults />} />
        <Route path="/AeroSearchUI" element={<AeroSearchUI />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/action" element={<ActionPage />} />

        {/* PROTECTED ROUTES */}
        <Route
          path="/homepage"
          element={
            <ProtectedRoute authInitialized={authInitialized}>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="profile" element={<Profile />} />
          <Route path="choose-listing" element={<PGorRentDialog />} />
          <Route path="list-your-space" element={<HouseListingForm />} />
          <Route path="list-rent" element={<RentListingForm />} />
          <Route path="buy-packs" element={<BuyPacks />} />
          <Route path="about-us" element={<AboutUs />} />
          <Route path="support" element={<NeedSupport />} />
          <Route path="pg-listing" element={<PGListing />} />
          <Route path="rent-listing" element={<RentListing />} />
          <Route path="offers" element={<OffersPage />} />
          <Route path="listings" element={<MyListings />} />
        </Route>

      </Routes>
    </AnimatePresence>
  );
}

// -----------------------
// App Component
// -----------------------
function App() {
  const navigate = useNavigate();
  const [redirectProcessed, setRedirectProcessed] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        // 🔁 Google redirect login
        const result = await getRedirectResult(auth);
        if (result?.user) {
          console.log("Google redirect user:", result.user.uid);
          navigate("/homepage");
          return;
        }

        // 🔐 App Check
        await logAppCheckToken();

        // 🔎 Wait for Auth state to initialize
        onAuthStateChanged(auth, async (user) => {
          setAuthInitialized(true);

          if (user) {
            const userDocRef = doc(db, "user-data", user.uid);
            const userSnapshot = await getDoc(userDocRef);
            if (userSnapshot.exists()) {
              console.log("User document:", userSnapshot.data());
            }
          }
          setRedirectProcessed(true);
        });

      } catch (err) {
        console.error("Firestore or redirect error:", err);
        toast.error("Something went wrong.");
        setRedirectProcessed(true);
      }
    };

    init();
  }, [navigate]);

  if (!redirectProcessed) {
    return <div style={{ marginTop: "50px", textAlign: "center" }}>Loading...</div>;
  }

  return (
    <HelmetProvider>
      <AnimatedRoutes authInitialized={authInitialized} />
    </HelmetProvider>
  );
}

export default App;
